import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "../api";
import { useStudentAuth } from "../StudentAuthContext";

export default function StudentRegister() {
  const { setSession } = useStudentAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = location.state?.next || "/book";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      const data = await api.studentRegister(email, password, name);
      setSession(data.token, data.student);
      navigate(next, { replace: true });
    } catch (err) {
      setError(err.message || "Could not create account");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="min-h-[100dvh] bg-[#FAF9F6] pt-24 md:pt-32 pb-12 px-6" data-testid="student-register-page">
      <div className="max-w-md mx-auto">
        <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-4">
          Create account
        </div>
        <h1 className="font-display text-4xl md:text-5xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
          Let's get started.
        </h1>
        <p className="mt-4 text-base text-[#1C1C1C]/70 leading-relaxed">
          You'll use this account to book lessons and access your virtual classroom.
        </p>

        <form onSubmit={onSubmit} className="mt-10 space-y-4">
          <Field label="Your name">
            <input
              data-testid="student-register-name"
              autoFocus
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Email">
            <input
              data-testid="student-register-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Password (min 8 characters)">
            <input
              data-testid="student-register-password"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
            />
          </Field>
          {error && <div className="text-sm text-[#B83A3A]" data-testid="student-register-error">{error}</div>}
          <button
            data-testid="student-register-submit"
            type="submit"
            disabled={loading}
            className="w-full px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-sm tracking-[0.16em] uppercase font-medium hover:bg-[#1C1C1C]/85 disabled:opacity-40 transition-colors"
          >
            {loading ? "Creating…" : "Create account"}
          </button>
        </form>

        <p className="mt-8 text-sm text-[#1C1C1C]/65">
          Already have an account?{" "}
          <Link
            to="/login"
            state={{ next }}
            className="underline underline-offset-4 text-[#1C1C1C]"
            data-testid="student-register-login-link"
          >
            Sign in
          </Link>
        </p>
        <style>{`.input{width:100%;padding:.75rem 1rem;background:white;border:1px solid rgba(28,28,28,0.1);border-radius:.75rem;font-size:1rem;outline:none}.input:focus{border-color:rgba(28,28,28,0.4)}`}</style>
      </div>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/60 mb-2">{label}</div>
      {children}
    </label>
  );
}
